import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SparklesIcon, ArrowUpRightIcon, FolderIcon, RotateCcwIcon, CheckCircle2Icon } from 'lucide-react';
import { RuleEditor, RuleSummary } from './SellingRulesManager';
import { EMPTY_RULE, SellingRule, catKey, itemKey, removeRule, saveRule, targetPool, useSelling } from '../../data/sellingRules';
interface Props {
  itemId: string;
  itemTitle: string;
  /** The merchant category this item currently sits in. */
  category?: string;
  kind: string;
  /** The merchant's own categories, for the picker. */
  categories: string[];
  /** Opens the category-level manager, focused on this item's category. */
  onOpenCategoryRules: (category?: string) => void;
}

/**
 * Product-level recommendations, deliberately kept small.
 *
 * By default this is a read-out: which rule this item is using and what it
 * recommends. Configuring 300 products one at a time is the thing we're
 * avoiding, so the editor only appears if the merchant asks for an exception.
 */
export function SellingRecommendations({
  itemId,
  itemTitle,
  category,
  kind,
  categories,
  onOpenCategoryRules
}: Props) {
  const {
    strategy,
    rules
  } = useSelling();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<SellingRule>(EMPTY_RULE);
  const own = rules[itemKey(itemId)];
  const inherited = category ? rules[catKey(category)] : undefined;
  const effective = own ?? inherited;
  const pool = useMemo(() => targetPool(kind, categories), [kind, categories]);
  const strategyNote = `Talab raises these using your shop setting — ${strategy.style.toLowerCase()}, up to ${strategy.max} per conversation.`;
  const startEditing = () => {
    setDraft(own ?? inherited ?? EMPTY_RULE);
    setEditing(true);
  };
  return <section className="rounded-2xl border border-ai-purple/20 bg-ai-purple/[0.02] p-5" data-id="element-1154" data-mp="2lm2l7m6804ds">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1155" data-mp="2pp8ruux8vxks">
        <div className="flex items-start gap-3 min-w-0" data-id="element-1156" data-mp="w69sq22spx3k">
          <div className="w-9 h-9 rounded-xl bg-ai-purple/10 flex items-center justify-center flex-shrink-0" data-id="element-1157" data-mp="w7ta5w06ue6x">
            <ArrowUpRightIcon className="w-[18px] h-[18px] text-ai-purple" data-id="element-1158" data-mp="r8zz0a8diz9x" />
          </div>
          <div className="min-w-0" data-id="element-1159" data-mp="mduffi2g1wp0">
            <h4 className="font-display font-bold text-[17px] text-dark-navy leading-tight" data-id="element-1160" data-mp="hhdqilnf7eav">
              Selling recommendations
            </h4>
            <p className="text-[13px] text-slate-500 mt-1 leading-relaxed" data-id="element-1161" data-mp="dgk27r62fxhg">
              What Talab may suggest with or instead of {itemTitle}.
            </p>
          </div>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold flex-shrink-0 ${own ? 'bg-ai-purple/10 text-ai-purple' : effective ? 'bg-success/10 text-success' : 'bg-slate-100 text-slate-500'}`} data-id="element-1162" data-mp="3hl06rft303f">
          {own ? 'Custom for this item' : effective ? 'Using category recommendations' : 'Using shop defaults'}
        </span>
      </div>

      {!editing ? <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-4" data-id="element-1163" data-mp="25elhnfqztalt">
          {effective ? <>
              {!own && category && <p className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500" data-id="element-1164" data-mp="2a63je60ubf7u">
                  <FolderIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-1165" data-mp="2f3n0ddoxa63h" />
                  From your {category} recommendations
                </p>}
              <div className={own ? '' : 'mt-2.5'} data-id="element-1166" data-mp="1mgseqdgdd0of">
                <RuleSummary rule={effective} data-id="element-1167" data-mp="1mhk9xn620we6" />
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4" data-id="element-1168" data-mp="1rd92v7nkjf1i">
                {own ? <>
                    <button onClick={startEditing} className="px-3.5 py-2 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-700 hover:border-ai-purple hover:text-ai-purple transition-colors duration-150 ease-out" data-id="element-1169" data-mp="1wbu4nbigy6s2">
                      Edit
                    </button>
                    <button onClick={() => removeRule(itemKey(itemId))} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-bold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-1170" data-mp="fmbb9oe9b94w">
                      <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-1171" data-mp="13iguwfdi7z6k" />
                      Reset to category recommendations
                    </button>
                  </> : <>
                    <button onClick={startEditing} className="px-3.5 py-2 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-700 hover:border-ai-purple hover:text-ai-purple transition-colors duration-150 ease-out" data-id="element-1172" data-mp="1caiukbfr1bfs">
                      Customize for this item
                    </button>
                    <button onClick={() => onOpenCategoryRules(category)} className="px-3.5 py-2 rounded-xl text-[13px] font-bold text-ai-purple hover:bg-ai-purple/[0.06] transition-colors duration-150 ease-out" data-id="element-1173" data-mp="20wfdni7bwfhq">
                      Edit {category} recommendations
                    </button>
                  </>}
              </div>
            </> : <>
              <p className="text-[13px] text-slate-500 leading-relaxed" data-id="element-1174" data-mp="1i4csbtjqsb15">
                No category recommendations configured
                {category ? ` for ${category}` : ''}. Setting them up once
                covers every item in the category.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-4" data-id="element-1175" data-mp="1d7mgxjivu3m2">
                <button onClick={() => onOpenCategoryRules(category)} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-ai-purple text-white text-[13px] font-bold hover:bg-purple-600 transition-colors duration-150 ease-out" data-id="element-1176" data-mp="z8eb47s7rx8w">
                  <SparklesIcon className="w-3.5 h-3.5" data-id="element-1177" data-mp="313u8k0epq77o" />
                  Set up category recommendations
                </button>
                <button onClick={startEditing} className="px-3.5 py-2.5 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-700 hover:border-slate-300 transition-colors duration-150 ease-out" data-id="element-1178" data-mp="2w624y94pgwnn">
                  Customize this item
                </button>
              </div>
            </>}
        </div> : <AnimatePresence initial={false} data-id="element-1179" data-mp="3eugvywf0waog">
          <motion.div initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-4" data-id="element-1180" data-mp="3auymcwsnjm0x">
            <p className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ai-purple" data-id="element-1181" data-mp="eyshfclqyoz1">
              <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-1182" data-mp="izm0nso8vfll" />
              This will apply to {itemTitle} only
            </p>
            <div className="mt-3" data-id="element-1183" data-mp="nw2soxykee3y">
              <RuleEditor rule={draft} pool={pool} onChange={setDraft} strategyNote={strategyNote} data-id="element-1184" data-mp="spfel0bv06t0" />
            </div>
            <div className="flex items-center gap-2 mt-4" data-id="element-1185" data-mp="12mk1j2dj46av">
              <button onClick={() => {
            saveRule(itemKey(itemId), draft);
            setEditing(false);
          }} className="px-5 py-2.5 rounded-xl bg-ai-purple text-white text-[13px] font-bold hover:bg-purple-600 transition-colors duration-150 ease-out" data-id="element-1186" data-mp="12p69h34c0ntm">
                Save for this item
              </button>
              <button onClick={() => setEditing(false)} className="px-4 py-2.5 text-[13px] font-semibold text-slate-600 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-1187" data-mp="3dfd3yyl7qltq">
                Cancel
              </button>
            </div>
          </motion.div>
        </AnimatePresence>}
    </section>;
}